import { Button, Heading } from '@lmc/ui-kit'
import Divider from '@lmc/ui-kit/src/layout/Divider/Divider'
import { DotLottieReact } from '@lottiefiles/dotlottie-react'
import type { DotLottie } from '@lottiefiles/dotlottie-web'
import { Modal } from 'antd'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import styled, { keyframes } from 'styled-components'
import GoalStarIcon from '../GoalStarIcon'

export type GoalCompletedModalProps = {
  open: boolean
  onClose: (event: React.MouseEvent<HTMLButtonElement>) => void
}

const GoalCompletedModal = ({ open, onClose }: GoalCompletedModalProps) => {
  const { t } = useTranslation()
  const [dotLottie, setDotLottie] = useState<DotLottie | null>(null)

  const dotLottieRefCallback = (dotLottie: DotLottie | null) => {
    setDotLottie(dotLottie)
  }

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    const playAnimation = () => dotLottie?.play?.()
    let timeout: ReturnType<typeof setTimeout>

    if (open) {
      timeout = setTimeout(playAnimation, 200)
    }

    return function cleanup() {
      clearTimeout(timeout)
    }
  }, [open, dotLottie])

  return (
    <Modal
      centered
      destroyOnClose
      width={470}
      footer={
        <ModalFooter>
          <Button onClick={onClose} variant="primary">
            {t('CONFIRM')}
          </Button>
        </ModalFooter>
      }
      open={open}
      onCancel={onClose}
    >
      <Subtitle level={3}>{t('GOAL_COMPLETED_CONGRATS')}</Subtitle>

      <StarContainer>
        <DotLottieReact
          dotLottieRefCallback={dotLottieRefCallback}
          speed={2}
          src="/lottie/star-animation.json"
          style={{ width: '150%', height: '150%' }}
        />
        <StyledStar />
      </StarContainer>

      <Title level={1}>{t('ACHIEVEMENT_LOGGED')}</Title>

      <StyledDivider />
    </Modal>
  )
}

export default GoalCompletedModal

const ModalFooter = styled.div`
  display: flex;
  gap: 16px;
`

const Title = styled(Heading)`
  color: ${(props) => props.theme.color.neutral.color3};
  text-align: center;
  display: flex;
  justify-content: center;
  align-items: center;
  font-weight: bold;
`

const Subtitle = styled(Heading)`
  color: ${(props) => props.theme.color.neutral.color3};
  text-align: left;
  display: flex;
  justify-content: flex-start;
  align-items: flex-start;
  font-weight: medium;
  padding-right: 24px;
`

const StarContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 305px;
  width: 100%;
  position: relative;
  margin: 24px auto 50px auto;

  & > * {
    position: absolute;
  }
`

const fadeInAnimation = keyframes`
  from {
    transform: scale(0);
  }

  to {
    transform: scale(1);
  }
`

const StyledStar = styled(GoalStarIcon)`
  transform: scale(0);
  animation: ${fadeInAnimation} 300ms ease-in 180ms forwards;
`

const StyledDivider = styled(Divider)`
  margin: 24px auto;
`
